/**
 * 零知识加密 v1 · 密码学原语
 *
 * 方案（与 apps/api/test/vault.e2e-spec.ts 契约一致）：
 * - KEK   = Argon2id(password, salt)（hash-wasm）
 * - authHash = hex(SHA-256(KEK || UTF8("lightchaser-auth")))
 * - DEK   = 32B 随机（AES-256-GCM 主密钥），以 KEK 包装存入 kdfParams.wrappedVaultKey
 * - nameKey = HKDF-SHA256(DEK, salt=32B 全零, info="lightchaser-name")
 * - dataKey = HKDF-SHA256(DEK, salt=32B 全零, info="lightchaser-data")
 * - 恢复码 RKEK = Argon2id(recoveryCode, recoverySalt)
 *   recoveryVerifier = hex(SHA-256(RKEK || UTF8("lightchaser-recovery")))
 *
 * 服务端只接触 authHash / recoveryVerifier / 密文，永不接触主密码与密钥明文。
 */
import { argon2id, sha256 } from 'hash-wasm';
import type { WrappedKeyJson } from './types';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

// ==================== base64 ====================

export function b64encode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

export function b64decode(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ==================== 随机 / 哈希 ====================

export function randomBytes(n: number): Uint8Array {
  const buf = new Uint8Array(n);
  crypto.getRandomValues(buf);
  return buf;
}

/** hex(SHA-256(...parts)) */
export async function sha256Hex(...parts: Uint8Array[]): Promise<string> {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const all = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    all.set(p, off);
    off += p.length;
  }
  return sha256(all);
}

// ==================== Argon2id（KEK / RKEK 派生） ====================

export interface Argon2Params {
  iterations: number;
  memorySize: number;
  parallelism: number;
  keyLength: number;
}

export const DEFAULT_ARGON2: Argon2Params = {
  iterations: 3,
  memorySize: 65536,
  parallelism: 4,
  keyLength: 32,
};

/** KEK = Argon2id(password, salt) */
export async function deriveKek(
  password: string,
  saltB64: string,
  params: Argon2Params = DEFAULT_ARGON2,
): Promise<Uint8Array> {
  return await argon2id({
    password,
    salt: b64decode(saltB64),
    iterations: params.iterations,
    memorySize: params.memorySize,
    parallelism: params.parallelism,
    hashLength: params.keyLength,
    outputType: 'binary',
  });
}

/** authHash = hex(SHA-256(KEK || UTF8("lightchaser-auth"))) */
export async function authHashOf(kek: Uint8Array): Promise<string> {
  return sha256Hex(kek, textEncoder.encode('lightchaser-auth'));
}

/** recoveryVerifier = hex(SHA-256(RKEK || UTF8("lightchaser-recovery"))) */
export async function recoveryVerifierOf(rkek: Uint8Array): Promise<string> {
  return sha256Hex(rkek, textEncoder.encode('lightchaser-recovery'));
}

// ==================== 子密钥派生（HKDF） ====================

const HKDF_SALT = new Uint8Array(32); // 约定：salt 为 32B 全零

/** nameKey / dataKey = HKDF-SHA256(DEK, salt=32B 全零, info) */
export async function deriveSubKey(dek: Uint8Array, info: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', dek.buffer as ArrayBuffer, 'HKDF', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: HKDF_SALT as unknown as BufferSource, info: textEncoder.encode(info) },
    base,
    256,
  );
  return crypto.subtle.importKey('raw', bits, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

// ==================== AES-256-GCM ====================

/** AES-256-GCM 加密 → { iv: base64(12B), ct: base64, tag: base64(16B) } */
export async function aesGcmEncrypt(
  key: CryptoKey,
  plaintext: Uint8Array,
): Promise<WrappedKeyJson> {
  const iv = randomBytes(12);
  const ctAll = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource },
      key,
      plaintext as unknown as BufferSource,
    ),
  );
  const ct = ctAll.slice(0, ctAll.length - 16);
  const tag = ctAll.slice(ctAll.length - 16);
  return { iv: b64encode(iv), ct: b64encode(ct), tag: b64encode(tag) };
}

/** AES-256-GCM 解密（WrappedKeyJson 形状） */
export async function aesGcmDecrypt(key: CryptoKey, wrapped: WrappedKeyJson): Promise<Uint8Array> {
  const iv = b64decode(wrapped.iv);
  const ct = b64decode(wrapped.ct);
  const tag = b64decode(wrapped.tag);
  const all = new Uint8Array(ct.length + tag.length);
  all.set(ct, 0);
  all.set(tag, ct.length);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource },
    key,
    all as unknown as BufferSource,
  );
  return new Uint8Array(plain);
}

/** 用 32B 原始密钥（KEK/RKEK）包装 DEK → WrappedKeyJson */
export async function wrapKeyBytes(wrappingKey: Uint8Array, dek: Uint8Array): Promise<WrappedKeyJson> {
  const key = await crypto.subtle.importKey(
    'raw',
    wrappingKey.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
  return aesGcmEncrypt(key, dek);
}

/** 解包 DEK */
export async function unwrapKeyBytes(wrappingKey: Uint8Array, wrapped: WrappedKeyJson): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    'raw',
    wrappingKey.buffer as ArrayBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
  return aesGcmDecrypt(key, wrapped);
}

// ==================== 文本便捷封装 ====================

export async function encryptText(key: CryptoKey, text: string): Promise<WrappedKeyJson> {
  return aesGcmEncrypt(key, textEncoder.encode(text));
}

export async function decryptText(key: CryptoKey, wrapped: WrappedKeyJson): Promise<string> {
  return textDecoder.decode(await aesGcmDecrypt(key, wrapped));
}
